import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, PLATFORM_ID, afterNextRender, computed, inject, signal, ViewChildren, QueryList, ElementRef, effect, DestroyRef } from '@angular/core';
import { CommonModule, isPlatformBrowser, Location, NgOptimizedImage } from '@angular/common';
import { SignalsStoreService } from '../shared/signals-store.service';
import { SidebarComponent } from '../shared/sidebar/sidebar.component';
import { ProductsService } from '../product/products.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toObservable } from '@angular/core/rxjs-interop';
import { auditTime, bufferWhen, catchError, debounceTime, filter, from, map, mergeMap, of, scan, startWith, Subject, switchMap, tap, timer, toArray } from 'rxjs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { StockService } from '../stock/stock.service';
import { MEDIUM } from "../../scss/responsive/responsive";
import { HeaderService } from '../shared/header/header.service';
import { ResolutionService } from '../shared/resolution.service';
import { EmptyMessageComponent } from '../shared/empty-message/empty-message.component';
import { ShopService } from './shop.service';
import { MenuService } from '../shared/menu.service';
import { environment } from '../../environments/environment';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { ActivatedRoute } from '@angular/router';
import { formatStringForURL } from '../shared/common/utils';
import { ProductCardV2Component } from '../shared/product-card-v2/product-card-v2.component';
import { Product, ProductResume } from '../product/product.types';
import { CanonicalService } from '../shared/canonical.service';
import { LocalStorageService } from '../shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from '../shared/constants/databases';

@Component({
  selector: 'app-shop',
  templateUrl: './shop.component.html',
  styleUrl: './shop.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SidebarComponent,
    ProductCardV2Component,
    NgbModule,
    MatProgressBarModule,
    EmptyMessageComponent,
    MatFormFieldModule,
    MatChipsModule,
    NgOptimizedImage,
  ]
})
export class ShopComponent implements OnInit, OnDestroy {

  @ViewChildren('subcategory')
  subcategories!: QueryList<ElementRef>;

  private productsService = inject(ProductsService);
  private headerService = inject(HeaderService);
  private resolutionService = inject(ResolutionService);
  private activatedRoute = inject(ActivatedRoute);
  private location = inject(Location);
  #menuService = inject(MenuService);
  #shopService = inject(ShopService);
  #canonicalService = inject(CanonicalService);
  #localStorageService = inject(LocalStorageService);
  private destroyRef = inject(DestroyRef);

  categoryBanner = computed(() => this.#shopService.categoryBanner());

  signalsStore = inject(SignalsStoreService);
  stockService = inject(StockService);

  isSidebarOpen = this.signalsStore.isSidebarOpen;
  hasSession = this.signalsStore.hasSession;
  selectedCategory = this.signalsStore.selectedCategory;

  isCollapseButtonHovered = signal(false);
  lastSubcategory: string | null = null;
  lastCategory: string | null = null;

  // Enhanced products signal that uses product-card-v2
  productsSignal = computed(() => this.#setUpProductsSignal());


  // Product details cache for enhanced loading
  productDetails = signal<Map<number, Product>>(new Map());

  #visibleProducts$ = new Subject<number[]>(); // IDs of products that are visible in the viewport

  categoriesSignal: any = computed(() => this.productsService.productsSignal());
  marketStatus = computed(() => this.signalsStore.marketStatus());
  closedMarket = computed(() => {
    const ms = this.marketStatus();
    return {
      title: ms.bannerTitle || `Our Online Farmer's Market Opens ${ms.openingDay.toUpperCase()} Morning!`,
      legend: ms.bannerContent || `We're busy sourcing next week's availability! Check back ${ms?.openingDay || 'Friday'} to begin shopping.`
    };
  })
  sidebarChanged = toObservable(this.signalsStore.sidebarFilterChanged);

  isContentLoading = computed(() => this.stockService.isLoading() || this.productsService.isLoading());
  isContentLoaded = computed(() => this.stockService.isLoaded() || this.productsService.isLoaded());

  // Effect que se ejecuta cuando ambos computed lleguen a su estado final
  private contentLoadingEffect = effect(() => {
    const isLoading = this.isContentLoading();
    const isLoaded = this.isContentLoaded();

    // Ejecutar acción cuando ya no está cargando Y está cargado
    if (!isLoading && isLoaded) {
      if (!isPlatformBrowser(this.platformId)) return;
      const selectedProductId = localStorage.getItem('selectedProductId');
      if (selectedProductId != null) {
        this.#goToSelectedProduct();
      } else {
        setTimeout(() => {
          this.#handleRouteChanges();
        }, 1);
      }
    }
  });

  isMobile = computed(() => this.resolutionService.isMobile());

  emptyMessage = 'No products were found.';
  hasGlobalMessages = computed(() => !!this.signalsStore.globalMessages().length);

  //#region flows config
  displayCategoryName = signal(environment.config.flows.shop.displayCategoryName);
  //#endregion

  filters = computed(() => this.signalsStore.getFilters());

  // Clear product details cache when category changes to prevent unbounded memory growth
  private clearDetailsEffect = effect(() => {
    this.selectedCategory();
    this.productDetails.set(new Map());
  });

  // Re-observe subcategory elements when products change for anchor link tracking
  private observeSubcategoriesEffect = effect(() => {
    this.productsSignal();
    setTimeout(() => this.#observeSubcategories(), 0);
  });

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    afterNextRender(() => {
      const isMobile = window.innerWidth <= MEDIUM;
      if (isMobile) this.signalsStore.isSidebarOpen.set(false);
    });
  }

  ngOnInit(): void {
    this.signalsStore.setIsInShopComponent(true);

    this.sidebarChanged
      .pipe(debounceTime(300), tap(() => this.productsService.filterBySidebarChange()), takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.headerService.producerRedirection();

    this.activatedRoute.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.#setUpSEOTitle());

    this.#setupBatchRequests();
  }

  #setUpSEOTitle(): void {
    const params = this.activatedRoute.snapshot.paramMap;
    const subcategorySlug = params.get('subcategory');
    const categorySlug = params.get('category');
    const activeSlug = subcategorySlug ?? categorySlug;
    const appName = environment.application.name;
    const { baseURL } = environment.config.seo;

    let description: string;
    let pageTitle: string;
    if (activeSlug) {
      const prettyName = activeSlug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      description = `Shop ${prettyName} from Locavore Delivery — Colorado's home delivery service for pasture-raised meats and local goods.`;
      pageTitle = `${prettyName} | ${appName}`;
    } else {
      description = `Shop pasture-raised meats, poultry, wild-caught seafood and local goods delivered across Colorado by Locavore Delivery.`;
      pageTitle = `Shop | ${appName}`;
    }

    const urlPath = `shop${[categorySlug, subcategorySlug].filter(Boolean).map(s => `/${s}`).join('')}`;

    this.#canonicalService.setPageTitle(pageTitle);
    this.#canonicalService.setPageUrl(`${baseURL}${urlPath}`);
    this.#canonicalService.setPageDescription(description);
    this.#canonicalService.setPageImage(`${baseURL}assets/images/landing/home/bg-hero.webp`);
    this.#canonicalService.setPageType('website');
  }

  scrollIntoView() {
    // Sidebar emits outIsMenuReady from a setTimeout that also fires during SSR
    // (the resolver pre-populates the sidebar on the server). Scroll restore is
    // browser-only — without this guard `localStorage`/`document` below throw
    // "localStorage is not defined" on the server, which aborts SSR and breaks
    // hydration of the cards (duplicate/unclamped descriptions).
    if (!isPlatformBrowser(this.platformId)) return;

    const selectedProductId = localStorage.getItem('selectedProductId');
    if (selectedProductId != null) {
      this.#goToSelectedProduct();
    } else {
      this.#handleRouteChanges();
    }
  }

  #goToSelectedProduct() {
    if (!isPlatformBrowser(this.platformId)) return;

    const selectedProductId = localStorage.getItem('selectedProductId');
    if (!selectedProductId) return;

    let attempts = 0;
    const maxAttempts = 20;

    const tryScroll = () => {
      const element = document.getElementById(selectedProductId);
      if (element) {
        const parent = document.getElementById('layoutShopMain');
        parent?.scrollTo({
          top: element.offsetTop - 100,
          left: 0,
          behavior: 'instant'
        });
        localStorage.removeItem('selectedProductId');
        return;
      }
      if (++attempts < maxAttempts) {
        requestAnimationFrame(tryScroll);
      }
    };

    requestAnimationFrame(tryScroll);
  }


  onProductCardReady(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  onProductCardHover(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }


  toggleSidebar() {
    this.signalsStore.isSidebarOpen.update((currentValue) => !currentValue);
  }

  toggleCollapseBarHover(hovered: boolean) {
    this.isCollapseButtonHovered.set(hovered);
  }

  ngOnDestroy(): void {
    this.headerService.clearFilters(true, false);
    this.signalsStore.setIsInShopComponent(false);
    this.#subcategoryObserver?.disconnect();
  }

  trackByFilterKey(_index: number, entry: [string, any]): string {
    return entry[0];
  }

  scrollToTop(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const scrollToTop = document.getElementById('scroll-to-top-ref');
    if (scrollToTop) {
      scrollToTop.scrollIntoView({ block: 'start' });
    }
  }

  // Persisted across reloads so the first paint reserves the correct banner
  // height (the API doesn't return dimensions, so without this we'd render with
  // the generic 12:1 fallback and reflow slightly when each real per-banner
  // image loads → CLS).
  #bannerDimensionsCache = new Map<string, { width: number; height: number }>(
    Object.entries(
      this.#localStorageService.get<Record<string, { width: number; height: number }>>(LOCALSTORAGE_KEYS.BANNER_DIMENSIONS) ?? {}
    )
  );

  getCategoryBanner(categoryId: number): { banner: string; url: string; width: number; height: number } | null {

    const category = this.#menuService.menu().find(x => x.id === categoryId);

    const banner = category?.banners?.[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner) return null;

    const cached = this.#bannerDimensionsCache.get(banner);
    return {
      banner,
      url: category.banners.url,
      width: category.banners.width ?? cached?.width ?? 1200,
      // 12:1 fallback (1200×100) matches these wide/short banners. The old 3:1
      // (1200×400) over-reserved ~4× the height on first paint/SSR (no cache),
      // showing a tall white gap that collapsed when the real image loaded.
      // The localStorage cache still refines this to the exact per-banner size.
      height: category.banners.height ?? cached?.height ?? 100
    };
  }

  getSubCategoryBanner(categoryId: number, subCategoryId: number): { banner: string; url: string; width: number; height: number } | null {

    const category = this.#menuService.menu().find(x => x.id === categoryId);

    if (!category) return null;

    const subcategory = category.subCategories
      .find((x: any) => x.id === subCategoryId);

    const banner = subcategory?.banners?.[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner) return null;

    const cached = this.#bannerDimensionsCache.get(banner);
    return {
      banner,
      url: subcategory.banners.url,
      width: subcategory.banners.width ?? cached?.width ?? 1200,
      // 12:1 fallback — see getCategoryBanner for the rationale.
      height: subcategory.banners.height ?? cached?.height ?? 100
    };
  }

  onBannerLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (!img.naturalWidth || !img.naturalHeight) return;

    const src = img.src;
    if (this.#bannerDimensionsCache.has(src)) return;

    this.#bannerDimensionsCache.set(src, {
      width: img.naturalWidth,
      height: img.naturalHeight
    });
    img.setAttribute('width', String(img.naturalWidth));
    img.setAttribute('height', String(img.naturalHeight));
    this.#localStorageService.set(
      LOCALSTORAGE_KEYS.BANNER_DIMENSIONS,
      Object.fromEntries(this.#bannerDimensionsCache)
    );
  }

  removeChipFilter(key: string) {
    if (key && this.filters().has(key)) {
      const type = this.filters().get(key)?.type;
      if (!type) return;

      this.signalsStore.setFilterChipRemoved(key);
    }
  }

  #handleRouteChanges() {
    setTimeout(() => {
      const category = this.activatedRoute.snapshot.paramMap.get('category');
      const subcategory = this.activatedRoute.snapshot.paramMap.get('subcategory');
      const subcategorySon = this.activatedRoute.snapshot.paramMap.get('slug');
      const routeArray = [];
      if (category) routeArray.push(category);
      if (subcategory) routeArray.push(subcategory);
      if (subcategorySon) routeArray.push(subcategorySon);

      const anchorLink = `/shop/${routeArray.join('/')}`;

      const element: any = document.querySelector(`[shop-data-anchor-link="${anchorLink}"]`);
      if (!element) {
        return
      };
      const parent = document.getElementById('layoutShopMain');
      parent?.scrollTo({
        top: (element.offsetTop),
        left: 0,
        behavior: 'instant'
      });
    }, 1);
  }

  changeAnchorLink(anchorLink: string) {
    this.location.replaceState(anchorLink);
  }

  // IntersectionObserver for subcategory anchor link tracking
  #subcategoryObserver: IntersectionObserver | null = null;
  #currentAnchorLink: string | null = null;

  #setupSubcategoryObserver() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.#subcategoryObserver?.disconnect();

    const container = document.getElementById('layoutShopMain');
    if (!container) return;

    this.#subcategoryObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const anchorLink = entry.target.getAttribute('shop-data-anchor-link');
            if (anchorLink && anchorLink !== this.#currentAnchorLink) {
              this.#currentAnchorLink = anchorLink;
              this.changeAnchorLink(anchorLink);
            }
          }
        }
      },
      {
        root: container,
        rootMargin: '-10% 0px -80% 0px',
        threshold: 0
      }
    );
  }

  #observeSubcategories() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.#subcategoryObserver) this.#setupSubcategoryObserver();
    if (!this.#subcategoryObserver || !this.subcategories?.length) return;

    this.#subcategoryObserver.disconnect();
    this.subcategories.forEach(el => {
      this.#subcategoryObserver!.observe(el.nativeElement);
    });
  }

  #validateBanner(item: any): { banner: string, url: string, show: boolean } {

    const banner = item?.banners[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner)
      return { banner: '', url: '', show: true };

    return {
      banner,
      url: item.banners.url,
      show: true
    };
  }

  #setupProductAnchorLinks(product: any) {
    const anchorLinkArray = ['shop'];

    if (product.category.name) {
      anchorLinkArray.push(formatStringForURL(product.category.name));
      const categoryAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.categoryAnchorLink = categoryAnchorLink;
    }

    if (product.subcategory?.name) {
      anchorLinkArray.push(formatStringForURL(product.subcategory.name));
      const subcategoryAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.subcategoryAnchorLink = subcategoryAnchorLink;
    }

    if (product.subcategorySon?.name) {
      anchorLinkArray.push(formatStringForURL(product.subcategorySon.name));
      const subcategorySonAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.subcategorySonAnchorLink = subcategorySonAnchorLink;
    }
  }

  #setUpProductsSignal() {
    const products = this.productsService.productsSignal();

    if (!products.length) return [];

    // Pre-build lookup map for O(1) category access instead of O(n) .find() per product
    const menu = this.#menuService.menu();
    const menuMap = new Map<number, any>();
    for (const cat of menu) {
      menuMap.set(cat.id, cat);
    }

    let lastCategory: number;
    let lastSubcategory: number;
    let lastSubcategorySon: number;

    return products.map((product: any) => {

      product.subCategorySonInView = null;
      product.categoryInView = null;
      product.subCategoryInView = null;

      let categoryId = 0,
        subCategoryId = 0,
        subCategorySonId = 0;

      if (product.category) {
        categoryId = +product.category.id;
        if (categoryId !== lastCategory) {
          product.categoryInView = this.#getCategoryBannerFromMap(menuMap, categoryId);
          lastCategory = categoryId;
        }
      }

      if (product.subcategory) {
        subCategoryId = +product.subcategory.id;
        if (subCategoryId !== lastSubcategory) {
          product.subCategoryInView = this.#getCategoryBannerFromMap(menuMap, categoryId, subCategoryId);
          lastSubcategory = subCategoryId;
        }
      }

      if (product.subcategorySon) {
        subCategorySonId = +product.subcategorySon.id;
        if (subCategorySonId !== lastSubcategorySon) {
          product.subCategorySonInView = this.#getCategoryBannerFromMap(menuMap, categoryId, subCategoryId, subCategorySonId);
          lastSubcategorySon = subCategorySonId;
        }
      }

      this.#setupProductAnchorLinks(product);

      return product as ProductResume;
    }) as ProductResume[];
  }

  #getCategoryBannerFromMap(menuMap: Map<number, any>, categoryId: number, subCategoryId?: number, subCategorySonId?: number) {
    let item = menuMap.get(categoryId);

    if (subCategoryId)
      item = item?.subCategories?.find((x: any) => x.id === subCategoryId);

    if (subCategorySonId)
      item = item?.subCategories?.find((x: any) => x.id === subCategorySonId);

    return this.#validateBanner(item);
  }

  #setupBatchRequests() {
    const BATCH_SIZE = 15;
    const MAX_CONCURRENT_REQUESTS = 2;

    const noActivity$ = this.#visibleProducts$.pipe(
      switchMap(() => timer(150).pipe(map(() => true))),
      startWith(false)
    );

    this.#visibleProducts$.pipe(
      bufferWhen(() => noActivity$.pipe(filter(inactive => inactive))),
      filter(productGroups => productGroups.length > 0),
      mergeMap(productGroups => productGroups.flat()),
      scan((acc: number[], productId: number) => [...new Set([...acc, productId])], []),
      auditTime(400),
      switchMap(productIds => {
        const idsToFetch = productIds.filter(id => !this.productDetails().has(id));

        if (idsToFetch.length === 0) return of([]);

        // Divide todos los productos en batches
        const chunks: number[][] = [];
        for (let i = 0; i < idsToFetch.length; i += BATCH_SIZE) {
          chunks.push(idsToFetch.slice(i, i + BATCH_SIZE));
        }

        // Invierte el orden: procesa los últimos productos primero (los que el usuario ve ahora)
        chunks.reverse();

        // Procesa todos los batches con límite de concurrencia
        return from(chunks).pipe(
          mergeMap(
            chunk => this.productsService.getProductsDetailsByIds(chunk).pipe(
              catchError(() => of({}))
            ),
            MAX_CONCURRENT_REQUESTS
          ),
          toArray()
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(responses => {
      this.productDetails.update(prev => {
        const updatedMap = new Map(prev);

        responses.forEach(details => {
          Object.entries(details).forEach(([index, detail]) => {
            const productDetail: Product = detail as Product;
            const productId = Number(productDetail.id);
            updatedMap.set(productId, productDetail);
          });
        });

        return updatedMap;
      });
    });
  }

}
